import React, { useState } from "react"

import cx from "classnames"
import { Message } from "@locmod/intl"
import { useBalance } from "wagmi"

import { Icon } from "components/ui"
import { Warning } from "components/feedback"
import ConnectButtonWrapper from "compositions/ConnectButtonWrapper/ConnectButtonWrapper"
import { useDetailedSxBetslip } from "@/contexts/DetailedSxBetslipContext"
import { useWallet } from "wallet"

import useChainProviderHook from "@/hooks/useChainProvider"
import { getSelectedTokenSymbol } from "@/utils/helper"

import SxCard from "./components/Card/SxCard"
import SxBetButton from "./components/BetButton/SxBetButton"
import SxChips from "./components/Chips/SxChips"
import AmountInput from "./components/AmountInput/AmountInput"
import { QuickBet, Slippage } from "./components"
import messages from "./messages"

const EmptyContent: React.FC = () => (
  <div className="max-w-64 text-center mx-auto my-6">
    <img
      className="size-16 mx-auto"
      src="/images/illustrations/betslip.webp"
      alt=""
    />
    <Message
      className="text-heading-h5 font-bold mt-4"
      value={messages.empty.title}
      tag="p"
    />
    <Message
      className="text-caption-13 mt-2 text-accent-grey-200"
      value={messages.empty.text}
      tag="p"
    />
  </div>
)

type SettingsProps = {
  onClose: () => void
}

const Settings: React.FC<SettingsProps> = ({ onClose }) => {
  return (
    <div>
      <div className="py-3 px-4 flex items-center justify-between">
        <Message
          className="text-caption-14 font-semibold"
          value={messages.settings}
        />
        <button
          className="text-accent-grey-200 hover:text-accent-grey-30 transition"
          onClick={onClose}
        >
          <Icon className="size-5" name="interface/close" />
        </button>
      </div>
      <div className="space-y-2">
        <Slippage />
        <QuickBet />
      </div>
    </div>
  )
}

const Content: React.FC<{ openSettings: () => void }> = ({ openSettings }) => {
  const { chainId, defaultToken } = useChainProviderHook()
  const { account: address } = useWallet()
  const {
    ticket,
    states,
    odds: oddsMap,
    disableReason,
    removeAll,
    maxBet,
    minBet,
    betAmount,
    isOddsFetching,
    isStatesFetching,
  } = useDetailedSxBetslip()

  const itemsLength = ticket.length
  const isSingle = itemsLength === 1

  // Get actual balance fetching state from wagmi for SX token
  const { data: balanceData, isFetching: isBalanceFetching } = useBalance({
    address,
    token: defaultToken?.address as `0x${string}`,
  })

  const { formatted: balance } = balanceData || {}

  // Eligibility by caps: must be >= minBet and <= maxBet
  const isAboveMin = betAmount
    ? typeof minBet !== "undefined"
      ? Number(betAmount) >= minBet
      : true
    : false
  const isBelowMax = betAmount
    ? typeof maxBet !== "undefined"
      ? Number(betAmount) <= maxBet
      : true
    : false

  // Check if user has enough balance for the bet amount (following the same pattern as other betslips)
  const isEnoughBalance =
    isBalanceFetching || !Boolean(+betAmount)
      ? true
      : Boolean(+balance! >= +betAmount)

  const handleRemoveAll = (event: React.MouseEvent) => {
    event.preventDefault()
    removeAll()
    // changeBetAmount(''); // Not needed, AmountInput handles this
  }

  return (
    <div>
      <div
        className={cx("px-4 flex justify-between", {
          "py-3": isSingle,
          "pt-3": !isSingle,
        })}
      >
        {/* SX only supports single bets, so always show single bet label */}
        <button className="cursor-default">
          <Message
            className="text-caption-14 font-semibold"
            value={messages.single}
          />
        </button>
        <div
          className={cx("flex items-center space-x-3 h-fit", {
            "pt-0.5": !isSingle,
          })}
        >
          <button
            className="text-accent-grey-200 hover:text-accent-grey-30 transition"
            onClick={openSettings}
          >
            <Icon className="size-5" name="interface/settings" />
          </button>
          <button
            className="text-accent-grey-200 hover:text-accent-grey-30 transition"
            onClick={handleRemoveAll}
          >
            <Icon className="size-5" name="interface/delete" />
          </button>
        </div>
      </div>
      <div
        className={cx("space-y-2 max-h-[24rem] overflow-auto no-scrollbar", {
          "pb-6": !isSingle,
          "pb-2": isSingle,
        })}
      >
        {ticket.map(item => {
          return (
            <SxCard
              key={item.marketHash}
              item={item}
              odds={item.odds} // TODO: ODDS should be based on the liquidity
              state={states[item.marketHash]}
              isOddsFetching={isOddsFetching}
              isStatesFetching={isStatesFetching}
            />
          )
        })}
      </div>
      <div
        className={cx({
          "-mt-4": !isSingle,
          "shadow-betslip rounded-lg": ticket.length > 2,
        })}
      >
        <div className="bg-accent-grey-850 p-3 rounded-lg z-10 relative">
          <>
            <AmountInput isEnoughBalance={isEnoughBalance} />
            <SxChips />
          </>
          {Boolean(disableReason) && (
            <Warning
              className="mt-3"
              text={
                disableReason && messages.warnings[disableReason]
                  ? {
                      ...messages.warnings[disableReason],
                      values: {
                        symbol: getSelectedTokenSymbol(chainId),
                        maxBet: maxBet?.toFixed(2) || "0.00",
                        minBet: minBet?.toFixed(2) || "0.00",
                      },
                    }
                  : ""
              }
            />
          )}
          <div className="mt-3">
            <ConnectButtonWrapper>
              <SxBetButton
                isEnoughBalance={isEnoughBalance}
                isBalanceFetching={isBalanceFetching}
              />
            </ConnectButtonWrapper>
          </div>
        </div>
      </div>
    </div>
  )
}

const SxBetslip: React.FC = () => {
  const { ticket } = useDetailedSxBetslip()
  const [isSettingsVisible, setSettingsVisible] = useState(false)

  if (!ticket.length) {
    return <EmptyContent />
  }
  return (
    <>
      {isSettingsVisible ? (
        <Settings onClose={() => setSettingsVisible(false)} />
      ) : (
        <Content openSettings={() => setSettingsVisible(true)} />
      )}
    </>
  )
}

export default SxBetslip
